import { CounterView } from './CounterView';
import type { CounterConfig } from './CounterView';
import { LivesCounterLayout } from '../../../../../layouts/common/LivesCounterLayout';
import type { AppStateController } from '../../../../app/state/AppStateController';
import { CountDownFormat, formatFixedCountdown, MS_IN_MINUTE } from '../../../../utils/time-utils';

enum LivesCounterLayoutState {
  Lives,
  UnlimitedTime,
}

const livesCounterConfig: CounterConfig = {
  initListener: (state, instance) => {
    const updateCounterView = (instantLayoutUpdate = false): void => {
      (instance as LivesCounterView).updateLives(
        state.getLives(),
        state.getUnlimitedLivesMinutes(),
        instantLayoutUpdate,
      );
    };

    updateCounterView(true);

    state.subscriptions.onChange(['lives'], () => updateCounterView, instance);

    const intervalId = setInterval(updateCounterView, 250);

    instance.on('destroyed', () => {
      clearInterval(intervalId);
      state.subscriptions.removeAllSubscriptions(instance);
    });
  },
};

export class LivesCounterView extends CounterView {
  private layoutState: LivesCounterLayoutState = LivesCounterLayoutState.Lives;

  constructor(state: AppStateController) {
    super(livesCounterConfig, new LivesCounterLayout(), state);
  }

  private setLayoutState(layoutState: LivesCounterLayoutState, forceUpdate = false): void {
    if (!forceUpdate && this.layoutState === layoutState) return;

    this.layoutState = layoutState;

    const { counterText, timeText, infinityIcon } = this.counterLayout;
    const duration = !forceUpdate ? 250 : 0;

    counterText.removeTweens();
    timeText.removeTweens();
    infinityIcon.removeTweens();

    if (layoutState === LivesCounterLayoutState.UnlimitedTime) {
      counterText.tweenTo({ alpha: 0 }, duration, 'quadOut', true);
      timeText.tweenTo({ alpha: 1 }, duration, 'quadOut', true, duration);
      infinityIcon.tweenTo({ alpha: 1 }, duration, 'quadOut', true, duration);
      this.interactive = false;
    } else {
      counterText.tweenTo({ alpha: 1 }, duration, 'quadOut', true, duration);
      timeText.tweenTo({ alpha: 0 }, duration, 'quadOut', true);
      infinityIcon.tweenTo({ alpha: 0 }, duration, 'quadOut', true);
      this.interactive = true;
    }
  }

  public updateLives(lives: number, unlimitedMinutes: number, forceLayoutUpdate = false): void {
    if (this.destroyed) return;

    if (unlimitedMinutes > 0) {
      this.setLayoutState(LivesCounterLayoutState.UnlimitedTime, forceLayoutUpdate);
      this.updateUnlimitedLivesTime(unlimitedMinutes);
    } else {
      this.setLayoutState(LivesCounterLayoutState.Lives, forceLayoutUpdate);
      this.updateAmount(lives);
    }
  }

  private updateUnlimitedLivesTime(unlimitedMinutes: number): void {
    const { timeText } = this.counterLayout;
    timeText.text = formatFixedCountdown(unlimitedMinutes * MS_IN_MINUTE, CountDownFormat.HMS);
  }

  get counterLayout(): LivesCounterLayout {
    return super.counterLayout as LivesCounterLayout;
  }
}
